import React from 'react';

/**
 * Loading spinner component for Suspense fallback
 * Used for route-level lazy loading
 */
export const PageLoader = () => (
  <div className="min-h-screen bg-black flex items-center justify-center">
    <div className="flex flex-col items-center space-y-4">
      <div className="w-12 h-12 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin"></div>
      <p className="text-gray-400 text-sm">Loading...</p>
    </div>
  </div>
);

/**
 * Minimal loader for component-level lazy loading
 * Less intrusive than full page loader
 */
export const ComponentLoader = ({ height = 'h-64' }) => (
  <div className={`${height} bg-gray-900/50 flex items-center justify-center`}>
    <div className="w-8 h-8 border-3 border-yellow-400 border-t-transparent rounded-full animate-spin"></div>
  </div>
);

/**
 * Skeleton loader for content sections
 */
export const SectionSkeleton = ({ rows = 3 }) => (
  <div className="animate-pulse space-y-4 p-6">
    <div className="h-8 bg-gray-700 rounded w-1/3"></div>
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="h-4 bg-gray-700 rounded w-full"></div>
    ))}
  </div>
);

/**
 * Higher-order component for lazy loading with intersection observer
 * Only loads component when it enters viewport
 */
export const withLazyLoad = (WrappedComponent, options = {}) => {
  const {
    rootMargin = '100px',
    threshold = 0.1,
    fallback = <ComponentLoader />
  } = options;

  return function LazyLoadedComponent(props) {
    const [isVisible, setIsVisible] = React.useState(false);
    const [hasLoaded, setHasLoaded] = React.useState(false);
    const containerRef = React.useRef(null);

    React.useEffect(() => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && !hasLoaded) {
            setIsVisible(true);
            setHasLoaded(true);
            observer.disconnect();
          }
        },
        { rootMargin, threshold }
      );

      if (containerRef.current) {
        observer.observe(containerRef.current);
      }

      return () => observer.disconnect();
    }, [hasLoaded]);

    return (
      <div ref={containerRef}>
        {isVisible ? <WrappedComponent {...props} /> : fallback}
      </div>
    );
  };
};

export default PageLoader;
